import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../common/integrations'

import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import {
  selectMeetingSummaryAgentDisplayablePresets,
  setMeetingSummaryDisplayablePresets,
  setMeetingSummaryDisplayedPresets,
} from '../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'

import { useMeetingSummary } from '../hooks'

import { MeetingSummaryAgentDropdownButton } from './DropdownButton/MeetingSummaryAgentDropdownButton'
import { MeetingSummaryHeaderPresetButton } from './PresetButton/MeetingSummaryHeaderPresetButton'
import { MeetingSummaryAgentCopyButton } from './CopyButton/MeetingSummaryAgentCopyButton'
import { MeetingSummaryAgentEditButton } from './EditButton/MeetingSummaryAgentEditButton'
import { MeetingSummaryAgentChatButton } from './ChatButton/MeetingSummaryAgentChatButton'
import { MeetingSummaryAgentCRMButton } from './CRMButton/MeetingSummaryAgentCRMButton'
import { MeetingSummaryAgentPDFButton } from './PDFButton/MeetingSummaryAgentPDFButton'
import { MeetingSummaryAgentDocumentButton } from './DocumentButton/MeetingSummaryAgentDocumentButton'
import { MEETING_SUMMARY_HEADER_PRESETS } from './HeaderPresets'

interface MeetingSummaryAgentHeaderProps {
  generatePDFBlob: () => Promise<Blob | null>
}

export const MeetingSummaryAgentHeader: React.FC<MeetingSummaryAgentHeaderProps> = ({ generatePDFBlob }) => {
  const { executeMeetingSummary } = useMeetingSummary()
  const dispatch = useAppDispatch()
  const displayablePresets = useAppSelector(selectMeetingSummaryAgentDisplayablePresets)
  const [stickied, setStickied] = useState<boolean>(false)
  const headerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    dispatch(setMeetingSummaryDisplayablePresets(MEETING_SUMMARY_HEADER_PRESETS))
    dispatch(setMeetingSummaryDisplayedPresets(MEETING_SUMMARY_HEADER_PRESETS.map((p) => p.aiFeaturePresetId)))
  }, [dispatch])

  const handleOnScroll = () => {
    if (headerRef.current?.getBoundingClientRect().top === 0) {
      setStickied(true)
    } else {
      setStickied(false)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll)
    return () => window.removeEventListener('scroll', handleOnScroll)
  }, [])

  return (
    <div
      ref={headerRef}
      className={classNames('d-flex pt-4 pb-3 meeting-summary-agent-header align-items-center gap-2 justify-content-between mb-5 flex-wrap bg-white', {
        'meeting-summary-agent-header--stickied': stickied,
      })}
    >
      <div className='d-flex align-items-center gap-2 '>
        <MeetingSummaryAgentCopyButton />

        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.SALESFORCE} label={getIntegrationDisplayName(IntegrationServiceId.SALESFORCE)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.PRACTIFI} label={getIntegrationDisplayName(IntegrationServiceId.PRACTIFI)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.XLR8} label={getIntegrationDisplayName(IntegrationServiceId.XLR8)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.WEALTHBOX} label={getIntegrationDisplayName(IntegrationServiceId.WEALTHBOX)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.HUBSPOT} label={getIntegrationDisplayName(IntegrationServiceId.HUBSPOT)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.REDTAIL} label={getIntegrationDisplayName(IntegrationServiceId.REDTAIL)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.DYNAMICS} label={getIntegrationDisplayName(IntegrationServiceId.DYNAMICS)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.ZOHO} label={getIntegrationDisplayName(IntegrationServiceId.ZOHO)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.SMARTOFFICE} label={getIntegrationDisplayName(IntegrationServiceId.SMARTOFFICE)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.ELEMENTS} label={getIntegrationDisplayName(IntegrationServiceId.ELEMENTS)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.BLACK_DIAMOND} label={getIntegrationDisplayName(IntegrationServiceId.BLACK_DIAMOND)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.ENGAGE} label={getIntegrationDisplayName(IntegrationServiceId.ENGAGE)} />
        <MeetingSummaryAgentCRMButton serviceId={IntegrationServiceId.ADVISOR_ENGINE} label={getIntegrationDisplayName(IntegrationServiceId.ADVISOR_ENGINE)} />
        <MeetingSummaryAgentPDFButton />
        <MeetingSummaryAgentDocumentButton generatePDFBlob={generatePDFBlob} />
        <MeetingSummaryAgentEditButton />
        <MeetingSummaryAgentDropdownButton executeMeetingSummary={executeMeetingSummary} />
      </div>
      <div className='d-flex gap-2 align-items-center'>
        <div className='d-flex gap-2'>
          <MeetingSummaryAgentChatButton />
        </div>
        <div className='d-flex gap-2'>
          {displayablePresets.map((headerPreset) => (
            <MeetingSummaryHeaderPresetButton key={headerPreset.aiFeaturePresetId} headerPreset={headerPreset} />
          ))}
        </div>
      </div>
    </div>
  )
}
